
/**
 * main.tsx
 * 
 * Main entry point for the SmartFarm application.
 * 
 * Change History:
 * 2025-05-09 - Fixed initialization order issues and circular dependencies
 * 2025-05-08 - Fixed module initialization order issue
 * 2025-05-06 - Added i18n initialization for internationalization support
 */

// Import CSS for early rendering visual improvements
import './global.css';
import './index.css';
import './App.css';
import './components.css';
import './lucide-icons.css';
import './navigation.css';

// Import console management utils - simplified version to avoid circular dependencies
import { initializeConsoleVisibility } from './utils/consoleManager';
import { safeInit } from './utils/moduleInit';

// Initialize console visibility first
try {
  // Use safeInit to avoid TDZ issues
  safeInit(() => initializeConsoleVisibility(), () => {});
} catch (error) {
  console.error('Failed to initialize console settings:', error);
}

// Show loading state immediately while bootstrapping the app
const rootElement = document.getElementById('root');
if (rootElement) {
  rootElement.innerHTML = `
    <div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: sans-serif;">
      <div style="text-align: center;">
        <div style="border: 4px solid #e2e8f0; border-top: 4px solid #4f46e5; border-radius: 50%; width: 40px; height: 40px; margin: 0 auto; animation: spin 1s linear infinite;"></div>
        <p style="margin-top: 16px; color: #4b5563;">Loading SmartFarm...</p>
      </div>
    </div>
    <style>
      @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
    </style>
  `;
}

// Use the bootstrap module for safe initialization
import initializeApp from './bootstrap';

// Wait a brief moment to ensure CSS has loaded for the loading indicator
setTimeout(() => {
  // Start initialization process
  initializeApp().catch(error => {
    console.error('Critical bootstrap error:', error);
    
    // Display error message to user if bootstrap fails
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="padding: 20px; font-family: sans-serif;">
          <h2 style="color: #e53e3e;">Application Error</h2>
          <p>We're sorry, but there was an error loading the application.</p>
          <p>Error details: ${error instanceof Error ? error.message : 'Unknown error'}</p>
          <button onclick="window.location.reload()" style="padding: 8px 16px; background: #4f46e5; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Reload Application
          </button>
        </div>
      `;
    }
  });
}, 300); // Short delay for loading indicator to render
